/**
 * Short link generation for WeHonor memorials.
 *
 * Two-tier URL strategy:
 *   Short  (30-day TTL): whnr.co/jennifer
 *   Permanent (forever): wehonor.com/monarch-funeral-home/dr-jennifer-laura-miano
 *
 * Short slugs prioritize the decedent's first name so families feel it's
 * personal. Falls back to first + last, then appends a random suffix if
 * there's still a collision within active memorials at the same funeral home.
 */

const SHORT_DOMAIN = "https://whnr.co";
const PERMANENT_DOMAIN = "https://wehonor.com";
const SHORT_LINK_TTL_DAYS = 30;

// --------------------------------------------------------------------------
// Slug utilities
// --------------------------------------------------------------------------

/** Strip accents, lowercase, replace non-alphanum with hyphens */
function toSlug(text: string): string {
  return text
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function extractFirstName(fullName: string): string {
  // Handle "Dr. Jennifer Laura Miano" → "jennifer"
  const cleaned = fullName.replace(/^(dr|mr|mrs|ms|prof|rev)\.?\s+/i, "").trim();
  const parts = cleaned.split(/\s+/);
  return toSlug(parts[0] ?? cleaned);
}

function extractLastName(fullName: string): string {
  const cleaned = fullName.replace(/^(dr|mr|mrs|ms|prof|rev)\.?\s+/i, "").trim();
  const parts = cleaned.split(/\s+/);
  return toSlug(parts[parts.length - 1] ?? cleaned);
}

/** Random 3-char alphanumeric suffix for rare collisions */
function randomSuffix(): string {
  return Math.random().toString(36).slice(2, 5);
}

// --------------------------------------------------------------------------
// Public API
// --------------------------------------------------------------------------

/**
 * Generate a short slug for a memorial, checking against existing active slugs.
 * Priority: first-name → first-last → first-last-XXX
 */
export function generateShortSlug(
  decedentName: string,
  existingSlugs: string[] = [],
): string {
  const taken = new Set(existingSlugs);

  const firstName = extractFirstName(decedentName);
  if (firstName && !taken.has(firstName)) return firstName;

  const firstLast = `${firstName}-${extractLastName(decedentName)}`;
  if (!taken.has(firstLast)) return firstLast;

  // Collision fallback — keep generating until unique
  let candidate = `${firstLast}-${randomSuffix()}`;
  while (taken.has(candidate)) {
    candidate = `${firstLast}-${randomSuffix()}`;
  }
  return candidate;
}

/** Full short URL: https://whnr.co/jennifer */
export function generateShortUrl(shortSlug: string): string {
  return `${SHORT_DOMAIN}/${shortSlug}`;
}

/** Full permanent URL: https://wehonor.com/monarch-funeral-home/dr-jennifer-laura-miano */
export function generatePermanentUrl(
  funeralHomeSlug: string,
  decedentSlug: string,
): string {
  return `${PERMANENT_DOMAIN}/${funeralHomeSlug}/${decedentSlug}`;
}

/** ISO date string for when the short link expires */
export function shortLinkExpiresAt(createdAt: string): string {
  const d = new Date(createdAt);
  d.setDate(d.getDate() + SHORT_LINK_TTL_DAYS);
  return d.toISOString();
}

/** Returns true if the short link has expired */
export function isShortLinkExpired(expiresAt: string): boolean {
  return new Date() > new Date(expiresAt);
}

/** Human-readable expiry: "Expires in 22 days" or "Expired" */
export function shortLinkExpiryLabel(expiresAt: string): string {
  const msLeft = new Date(expiresAt).getTime() - Date.now();
  if (msLeft <= 0) return "Expired";
  const daysLeft = Math.ceil(msLeft / (1000 * 60 * 60 * 24));
  return daysLeft === 1 ? "Expires tomorrow" : `Expires in ${daysLeft} days`;
}

/** Convert funeral home name to a URL-safe slug */
export function toFuneralHomeSlug(funeralHomeName: string): string {
  return toSlug(funeralHomeName);
}

/** Convert decedent name to a URL-safe slug */
export function toDecedentSlug(decedentName: string): string {
  return toSlug(decedentName);
}
